import { useEffect, useState, useCallback } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import {
  Plus,
  Search,
  Loader2,
  Trash2,
  Pencil,
  ChevronLeft,
  ChevronRight,
  Download,
  Upload,
  User as UserIcon,
  Building2,
} from 'lucide-react'
import { api } from '../lib/api'
import { downloadFile } from '../lib/download'
import ClientFormModal from '../components/clients/ClientFormModal'
import { useAuth } from '../contexts/AuthContext'
import type {
  ClientDetail,
  ClientListItem,
  ClientListResponse,
  ClientType,
} from '@toga/shared'

const PAGE_SIZE = 25

export default function Clients() {
  const auth = useAuth()
  const navigate = useNavigate()
  const canWrite = auth.status === 'authenticated' && auth.user.role !== 'LECTURA'
  const canImport =
    auth.status === 'authenticated' && (auth.user.role === 'ADMIN' || auth.user.role === 'ABOGADO')

  const [data, setData] = useState<ClientListResponse | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const [q, setQ] = useState('')
  const [typeFilter, setTypeFilter] = useState<ClientType | ''>('')
  const [page, setPage] = useState(1)

  const [editing, setEditing] = useState<ClientDetail | 'new' | null>(null)

  const reload = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const params = new URLSearchParams()
      if (q.trim()) params.set('q', q.trim())
      if (typeFilter) params.set('type', typeFilter)
      params.set('page', String(page))
      params.set('pageSize', String(PAGE_SIZE))
      const resp = await api.get<ClientListResponse>(`/clients?${params.toString()}`)
      setData(resp)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error al cargar')
    } finally {
      setLoading(false)
    }
  }, [q, typeFilter, page])

  useEffect(() => {
    void reload()
  }, [reload])

  // Volver a página 1 cuando cambian los filtros
  useEffect(() => {
    setPage(1)
  }, [q, typeFilter])

  const onEdit = async (id: string) => {
    try {
      const { client } = await api.get<{ client: ClientDetail }>(`/clients/${id}`)
      setEditing(client)
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Error')
    }
  }

  const onDelete = async (c: ClientListItem) => {
    if (!confirm(`¿Eliminar el cliente "${c.displayName}"?`)) return
    try {
      await api.delete(`/clients/${c.id}`)
      await reload()
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Error')
    }
  }

  const totalPages = data ? Math.max(1, Math.ceil(data.total / data.pageSize)) : 1

  const [exporting, setExporting] = useState(false)
  const onExport = async () => {
    setExporting(true)
    setError(null)
    try {
      await downloadFile('/export/clients.csv', 'clientes.csv')
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error al exportar')
    } finally {
      setExporting(false)
    }
  }

  return (
    <div className="space-y-6 animate-slideUp">
      <header className="flex flex-wrap items-end gap-3 justify-between">
        <div>
          <h2 className="text-2xl font-serif font-semibold t-primary">
            {data ? `${data.total} ` : ''}
            Cliente{data?.total === 1 ? '' : 's'}
          </h2>
          <p className="t-muted text-sm mt-1">Personas físicas y jurídicas del bufete.</p>
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => void onExport()}
            disabled={exporting}
            title="Descargar todos los clientes en CSV"
            className="px-3 py-2 rounded-lg text-xs border border-base t-secondary hover:bg-hover hover:text-toga-200 flex items-center gap-1.5 disabled:opacity-50"
          >
            {exporting ? <Loader2 size={12} className="animate-spin" /> : <Download size={12} />}
            Exportar CSV
          </button>
          {canImport && (
            <Link
              to="/clients/import"
              title="Importar clientes y contactos desde un Excel"
              className="px-3 py-2 rounded-lg text-xs border border-base t-secondary hover:bg-hover hover:text-toga-200 flex items-center gap-1.5"
            >
              <Upload size={12} />
              Importar Excel
            </Link>
          )}
          {canWrite && (
            <button
              type="button"
              onClick={() => setEditing('new')}
              className="px-3.5 py-2 rounded-lg text-sm font-semibold text-white bg-gradient-to-r from-toga-600 via-toga-500 to-toga-700 hover:shadow-glow-toga flex items-center gap-1.5"
            >
              <Plus size={16} /> Nuevo cliente
            </button>
          )}
        </div>
      </header>

      {/* Filtros */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[260px]">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Buscar por nombre, email, teléfono o NIF…"
            className="w-full pl-9 pr-3 py-2 rounded-full bg-slate-800/60 border border-slate-700/60 text-slate-200 placeholder-slate-500 focus:outline-none focus:ring-1 focus:ring-toga-500/40 focus:border-toga-500/60"
          />
        </div>
        <select
          value={typeFilter}
          onChange={(e) => setTypeFilter(e.target.value as ClientType | '')}
          className="px-3 py-2 rounded-full bg-slate-800/60 border border-slate-700/60 text-slate-200"
        >
          <option value="">Todos los tipos</option>
          <option value="PERSONA">Personas</option>
          <option value="EMPRESA">Empresas</option>
        </select>
      </div>

      {error && (
        <div className="text-sm rounded-lg px-3 py-2 border text-rose-300 bg-rose-500/10 border-rose-500/30">
          {error}
        </div>
      )}

      <div className="surface overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-xs uppercase tracking-wider t-faint border-b border-base">
              <th className="px-4 py-3 font-medium">Cliente</th>
              <th className="px-4 py-3 font-medium">NIF / CIF</th>
              <th className="px-4 py-3 font-medium">Contacto</th>
              <th className="px-4 py-3 font-medium">Ciudad</th>
              <th className="px-4 py-3 font-medium">Alta</th>
              <th className="px-4 py-3 font-medium text-right">Acciones</th>
            </tr>
          </thead>
          <tbody>
            {loading && !data ? (
              <tr>
                <td colSpan={6} className="px-4 py-10 text-center t-muted">
                  <Loader2 size={18} className="inline animate-spin mr-2" /> Cargando…
                </td>
              </tr>
            ) : data && data.items.length === 0 ? (
              <tr>
                <td colSpan={6} className="px-4 py-10 text-center t-muted">
                  {q || typeFilter ? (
                    'Sin resultados para esos filtros.'
                  ) : (
                    <>
                      Aún no hay clientes.
                      {canImport && (
                        <>
                          {' '}
                          <Link to="/clients/import" className="text-toga-300 hover:underline">
                            Impórtalos desde un Excel
                          </Link>
                          .
                        </>
                      )}
                    </>
                  )}
                </td>
              </tr>
            ) : (
              data?.items.map((c) => (
                <tr
                  key={c.id}
                  className="border-b border-base/50 last:border-b-0 hover:bg-hover/50 cursor-pointer"
                  onClick={() => navigate(`/clients/${c.id}`)}
                >
                  <td className="px-4 py-3 t-primary font-medium">
                    <div className="flex items-center gap-2.5">
                      <span
                        className={[
                          'w-8 h-8 rounded-lg grid place-items-center',
                          c.type === 'EMPRESA'
                            ? 'bg-indigo-500/15 text-indigo-300'
                            : 'bg-purple-500/15 text-purple-300',
                        ].join(' ')}
                      >
                        {c.type === 'EMPRESA' ? <Building2 size={14} /> : <UserIcon size={14} />}
                      </span>
                      <Link
                        to={`/clients/${c.id}`}
                        onClick={(e) => e.stopPropagation()}
                        className="hover:underline"
                      >
                        {c.displayName}
                      </Link>
                    </div>
                  </td>
                  <td className="px-4 py-3 t-secondary font-mono text-xs">{c.nationalId ?? '—'}</td>
                  <td className="px-4 py-3 t-secondary text-xs">
                    <div>{c.email ?? '—'}</div>
                    <div className="t-faint">{c.phone ?? ''}</div>
                  </td>
                  <td className="px-4 py-3 t-secondary text-xs">{c.city ?? '—'}</td>
                  <td className="px-4 py-3 t-muted text-xs">
                    {new Date(c.createdAt).toLocaleDateString('es-ES')}
                  </td>
                  <td className="px-4 py-3 text-right" onClick={(e) => e.stopPropagation()}>
                    <div className="inline-flex items-center gap-1">
                      <button
                        type="button"
                        onClick={() => onEdit(c.id)}
                        title="Editar datos"
                        className="w-8 h-8 grid place-items-center rounded-lg hover:bg-hover t-muted hover:text-toga-300"
                      >
                        <Pencil size={14} />
                      </button>
                      {canWrite && (
                        <button
                          type="button"
                          onClick={() => onDelete(c)}
                          title="Eliminar"
                          className="w-8 h-8 grid place-items-center rounded-lg hover:bg-rose-500/10 t-muted hover:text-rose-400"
                        >
                          <Trash2 size={14} />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>

        {/* Paginación */}
        {data && data.total > data.pageSize && (
          <div className="flex items-center justify-between px-4 py-3 border-t border-base text-sm">
            <span className="t-muted">
              Página {data.page} de {totalPages} · {data.total} clientes
            </span>
            <div className="flex gap-1">
              <button
                type="button"
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={data.page <= 1}
                className="w-8 h-8 grid place-items-center rounded-lg hover:bg-hover t-muted disabled:opacity-30"
              >
                <ChevronLeft size={16} />
              </button>
              <button
                type="button"
                onClick={() => setPage((p) => p + 1)}
                disabled={data.page >= totalPages}
                className="w-8 h-8 grid place-items-center rounded-lg hover:bg-hover t-muted disabled:opacity-30"
              >
                <ChevronRight size={16} />
              </button>
            </div>
          </div>
        )}
      </div>

      {editing !== null && (
        <ClientFormModal
          initial={editing === 'new' ? null : editing}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null)
            void reload()
          }}
        />
      )}
    </div>
  )
}
